import { useState } from 'react';
import Sidebar from '../dashboard/components/Sidebar';

const NewLeads = () => {
  const [selectedLead, setSelectedLead] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    source: 'website',
    status: 'new',
    estimatedValue: '',
    priority: 'medium',
    notes: ''
  });

  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    dateRange: 'all',
    startDate: '',
    endDate: '',
    status: 'all',
    includeNotes: true,
    includeContactInfo: true
  });

  const leads = [
    {
      id: 1,
      name: 'Sarah Johnson',
      company: 'Tech Innovations Inc.',
      email: 'sarah.j@techinnovations.com',
      phone: '+1 (555) 123-4567',
      source: 'Website',
      status: 'new',
      value: 45000,
      createdAt: '2024-12-10',
      notes: 'Interested in complete office renovation. Looking for modern design with sustainable materials.',
      priority: 'high'
    },
    {
      id: 2,
      name: 'Michael Chen',
      company: 'Green Solutions LLC',
      email: 'm.chen@greensolutions.com',
      phone: '+1 (555) 987-6543',
      source: 'Referral',
      status: 'contacted',
      value: 32000,
      createdAt: '2024-12-08',
      notes: 'Needs eco-friendly construction for new warehouse facility. Timeline is flexible.',
      priority: 'medium'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      company: 'Creative Studios',
      email: 'emily@creativestudios.com',
      phone: '+1 (555) 456-7890',
      source: 'LinkedIn',
      status: 'qualified',
      value: 78000,
      createdAt: '2024-12-05',
      notes: 'High-end residential project. Budget confirmed, ready to start planning phase.',
      priority: 'high'
    },
    {
      id: 4,
      name: 'David Thompson',
      company: 'Retail Dynamics',
      email: 'david.t@retaildynamics.com',
      phone: '+1 (555) 321-0987',
      source: 'Google Ads',
      status: 'new',
      value: 25000,
      createdAt: '2024-12-12',
      notes: 'Store renovation project. Needs quick turnaround for holiday season.',
      priority: 'medium'
    },
    {
      id: 5,
      name: 'Lisa Wang',
      company: 'Healthcare Partners',
      email: 'l.wang@healthcarepartners.com',
      phone: '+1 (555) 654-3210',
      source: 'Trade Show',
      status: 'proposal',
      value: 120000,
      createdAt: '2024-12-01',
      notes: 'Medical facility expansion. Requires specialized compliance and safety features.',
      priority: 'high'
    }
  ];

  const handleAddLead = () => {
    setShowAddModal(true);
  };

  const handleSaveLead = () => {
    // Here you would typically save to your backend
    console.log('Saving lead:', newLead);
    
    // Reset form and close modal
    setNewLead({
      name: '',
      company: '',
      email: '',
      phone: '',
      source: 'Website',
      status: 'new',
      value: '',
      priority: 'medium',
      notes: ''
    });
    setShowAddModal(false);
  };

  const handleCancelAdd = () => {
    setNewLead({
      name: '',
      company: '',
      email: '',
      phone: '',
      source: 'Website',
      status: 'new',
      value: '',
      priority: 'medium',
      notes: ''
    });
    setShowAddModal(false);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportConfirm = () => {
    // Simulate export process
    const exportData = leads.filter(lead => {
      // Filter by status
      if (exportOptions.status !== 'all' && lead.status !== exportOptions.status) {
        return false;
      }
      
      // Filter by date range
      if (exportOptions.dateRange === 'custom') {
        const leadDate = new Date(lead.createdAt);
        const startDate = new Date(exportOptions.startDate);
        const endDate = new Date(exportOptions.endDate);
        if (leadDate < startDate || leadDate > endDate) {
          return false;
        }
      } else if (exportOptions.dateRange === 'last30') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        if (new Date(lead.createdAt) < thirtyDaysAgo) {
          return false;
        }
      } else if (exportOptions.dateRange === 'last90') {
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        if (new Date(lead.createdAt) < ninetyDaysAgo) {
          return false;
        }
      }
      
      return true;
    });

    // Create export content based on format
    let content = '';
    let filename = '';
    let mimeType = '';

    if (exportOptions.format === 'csv') {
      const headers = ['Name', 'Company', 'Email'];
      if (exportOptions.includeContactInfo) headers.push('Phone');
      headers.push('Source', 'Status', 'Estimated Value', 'Priority', 'Created Date');
      if (exportOptions.includeNotes) headers.push('Notes');

      content = headers.join(',') + '\n';
      exportData.forEach(lead => {
        const row = [
          `"${lead.name}"`,
          `"${lead.company}"`,
          `"${lead.email}"`
        ];
        if (exportOptions.includeContactInfo) row.push(`"${lead.phone}"`);
        row.push(
          `"${lead.source}"`,
          `"${lead.status}"`,
          `"${lead.estimatedValue}"`,
          `"${lead.priority}"`,
          `"${lead.createdAt}"`
        );
        if (exportOptions.includeNotes) row.push(`"${lead.notes || ''}"`);
        content += row.join(',') + '\n';
      });
      filename = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    } else if (exportOptions.format === 'json') {
      const exportFields = exportData.map(lead => {
        const filtered: any = {
          name: lead.name,
          company: lead.company,
          email: lead.email,
          source: lead.source,
          status: lead.status,
          estimatedValue: lead.estimatedValue,
          priority: lead.priority,
          createdAt: lead.createdAt
        };
        if (exportOptions.includeContactInfo) filtered.phone = lead.phone;
        if (exportOptions.includeNotes) filtered.notes = lead.notes;
        return filtered;
      });
      content = JSON.stringify(exportFields, null, 2);
      filename = `leads_export_${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    }

    // Create and trigger download
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    setShowExportModal(false);
    
    // Show success message
    setTimeout(() => {
      alert(`Successfully exported ${exportData.length} leads as ${exportOptions.format.toUpperCase()}`);
    }, 100);
  };

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setExportOptions({
      format: 'csv',
      dateRange: 'all',
      startDate: '',
      endDate: '',
      status: 'all',
      includeNotes: true,
      includeContactInfo: true
    });
  };

  const isFormValid = newLead.name.trim() && newLead.email.trim() && newLead.company.trim();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'proposal': return 'bg-purple-100 text-purple-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const selectedLeadData = selectedLead ? leads.find(lead => lead.id === selectedLead) : null;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">New Leads</h1>
              <p className="text-sm text-gray-600 mt-1">Track and manage potential clients</p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleExport}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center whitespace-nowrap"
              >
                <i className="ri-download-line mr-2"></i>
                Export
              </button>
              <button 
                onClick={handleAddLead}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                Add Lead
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Leads List */}
          <div className="flex-1 flex flex-col">
            {/* Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {/* Leads Table */}
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Lead</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Company</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Source</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Value</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Priority</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className={`hover:bg-gray-50 cursor-pointer ${
                            selectedLead === lead.id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setSelectedLead(lead.id)}
                        >
                          <td className="py-4 px-4">
                            <div>
                              <div className="font-medium text-gray-900">{lead.name}</div>
                              <div className="text-sm text-gray-500">{lead.email}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-900">{lead.company}</td>
                          <td className="py-4 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {lead.source}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                              {lead.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm font-medium text-gray-900">
                            ${lead.value.toLocaleString()}
                          </td>
                          <td className="py-4 px-4">
                            <i className={`ri-flag-fill ${getPriorityColor(lead.priority)}`}></i>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-500">
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-800">
                                <i className="ri-edit-line"></i>
                              </button>
                              <button className="text-green-600 hover:text-green-800">
                                <i className="ri-phone-line"></i>
                              </button>
                              <button className="text-purple-600 hover:text-purple-800">
                                <i className="ri-mail-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Details Sidebar */}
          {selectedLeadData && (
            <div className="w-96 bg-white border-l border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Lead Details</h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Contact Info */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{selectedLeadData.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company</label>
                      <p className="text-gray-900">{selectedLeadData.company}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{selectedLeadData.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{selectedLeadData.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Lead Details */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Lead Details</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Source</label>
                      <p className="text-gray-900">{selectedLeadData.source}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedLeadData.status)}`}>
                        {selectedLeadData.status}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Estimated Value</label>
                      <p className="text-gray-900 font-medium">${selectedLeadData.value.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Priority</label>
                      <div className="flex items-center">
                        <i className={`ri-flag-fill mr-2 ${getPriorityColor(selectedLeadData.priority)}`}></i>
                        <span className="capitalize">{selectedLeadData.priority}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Notes</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">{selectedLeadData.notes}</p>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center whitespace-nowrap">
                    <i className="ri-phone-line mr-2"></i>
                    Call Lead
                  </button>
                  <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center justify-center whitespace-nowrap">
                    <i className="ri-mail-line mr-2"></i>
                    Send Email
                  </button>
                  <button className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 flex items-center justify-center whitespace-nowrap">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Meeting
                  </button>
                  <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 flex items-center justify-center whitespace-nowrap">
                    <i className="ri-edit-line mr-2"></i>
                    Edit Lead
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add New Lead</h2>
              <button
                onClick={handleCancelAdd}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newLead.name}
                      onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newLead.company}
                      onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={newLead.phone}
                      onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                {/* Lead Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Lead Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                    <select
                      value={newLead.source}
                      onChange={(e) => setNewLead({ ...newLead, source: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="Website">Website</option>
                      <option value="Referral">Referral</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Trade Show">Trade Show</option>
                      <option value="Cold Call">Cold Call</option>
                      <option value="Email Campaign">Email Campaign</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={newLead.status}
                      onChange={(e) => setNewLead({ ...newLead, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="proposal">Proposal</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Value</label>
                    <input
                      type="number"
                      value={newLead.value}
                      onChange={(e) => setNewLead({ ...newLead, value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Enter estimated value"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={newLead.priority}
                      onChange={(e) => setNewLead({ ...newLead, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter any additional notes about this lead..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
              <button
                onClick={handleCancelAdd}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLead}
                disabled={!isFormValid}
                className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                  isFormValid
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Export Leads</h3>
              <button
                onClick={handleCloseExportModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Export Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Export Format
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setExportOptions(prev => ({ ...prev, format: 'csv' }))}
                    className={`p-3 border rounded-lg text-left ${
                      exportOptions.format === 'csv'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center">
                      <i className="ri-file-text-line mr-2"></i>
                      <div>
                        <div className="font-medium">CSV</div>
                        <div className="text-xs text-gray-500">Spreadsheet format</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setExportOptions(prev => ({ ...prev, format: 'json' }))}
                    className={`p-3 border rounded-lg text-left ${
                      exportOptions.format === 'json'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center">
                      <i className="ri-code-line mr-2"></i>
                      <div>
                        <div className="font-medium">JSON</div>
                        <div className="text-xs text-gray-500">Data format</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range
                </label>
                <select
                  value={exportOptions.dateRange}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                >
                  <option value="all">All Time</option>
                  <option value="last30">Last 30 Days</option>
                  <option value="last90">Last 90 Days</option>
                  <option value="custom">Custom Range</option>
                </select>

                {exportOptions.dateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={exportOptions.startDate}
                        onChange={(e) => setExportOptions(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">End Date</label>
                      <input
                        type="date"
                        value={exportOptions.endDate}
                        onChange={(e) => setExportOptions(prev => ({ ...prev, endDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Status
                </label>
                <select
                  value={exportOptions.status}
                  onChange={(e) => setExportOptions(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Include Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Include in Export
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeContactInfo}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeContactInfo: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Contact Information</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeNotes}
                      onChange={(e) => setExportOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Notes and Comments</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseExportModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleExportConfirm}
                disabled={exportOptions.dateRange === 'custom' && (!exportOptions.startDate || !exportOptions.endDate)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-download-line mr-2"></i>
                Export Data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewLeads;
